import type { SelectChangeEvent } from "@mui/material";
import { MenuItem, Select, Stack } from "@mui/material";
import NumberTextField from "@entrilia/shared/components/inputs/NumberTextField";
import type { NumberObjectFilter, NumberObjectFilterOperator } from "../../api/types/objectTypes";
import { allNumberObjectFilterOperators } from "../../api/types/objectTypes";
import { getNumberFilterValues, numberFilterOperatorCaptions } from "./objectFilterHelpers";

interface Props {
  filter: NumberObjectFilter;
  onFilterChange: (filter: NumberObjectFilter) => void;
}

const operatorToValueControlTypeMap: Record<NumberObjectFilterOperator, "none" | "single" | "double"> = {
  Equals: "single",
  NotEquals: "single",
  Between: "double",
  GreaterThan: "single",
  GreaterThanOrEquals: "single",
  LessThan: "single",
  LessThanOrEquals: "single",
  Empty: "none",
  NotEmpty: "none",
};

const ObjectNumberFilterEditorContent = ({ filter, onFilterChange }: Props) => {
  const handleFilterOperatorChange = (event: SelectChangeEvent<string>) => {
    const newOperator = event.target.value as NumberObjectFilterOperator;
    onFilterChange({
      value: newOperator === "Equals" || newOperator === "NotEquals" ? filter.value : undefined,
      from:
        newOperator === "Between" || newOperator === "GreaterThan" || newOperator === "GreaterThanOrEquals"
          ? filter.from
          : undefined,
      to:
        newOperator === "Between" || newOperator === "LessThan" || newOperator === "LessThanOrEquals"
          ? filter.to
          : undefined,
      operator: newOperator,
    });
  };

  const handleSingleValueChange = (value: number | undefined) => {
    if (filter.operator === "GreaterThan" || filter.operator === "GreaterThanOrEquals") {
      onFilterChange({ ...filter, from: value });
    } else if (filter.operator === "LessThan" || filter.operator === "LessThanOrEquals") {
      onFilterChange({ ...filter, to: value });
    } else {
      onFilterChange({ ...filter, value });
    }
  };

  const handleFirstValueChange = (value: number | undefined) => {
    onFilterChange({ ...filter, from: value });
  };

  const handleSecondValueChange = (value: number | undefined) => {
    onFilterChange({ ...filter, to: value });
  };

  const valueControlType = operatorToValueControlTypeMap[filter.operator];

  const values = getNumberFilterValues(filter);
  const singleValue = values !== undefined && !Array.isArray(values) ? values : undefined;
  const fromValue = Array.isArray(values) ? values[0] : undefined;
  const toValue = Array.isArray(values) ? values[1] : undefined;

  return (
    <>
      <Select value={filter.operator} onChange={handleFilterOperatorChange} sx={{ flex: "0 0 12.5rem" }}>
        {allNumberObjectFilterOperators.map((operator) => (
          <MenuItem key={operator} value={operator}>
            {numberFilterOperatorCaptions[operator]}
          </MenuItem>
        ))}
      </Select>
      {valueControlType === "single" && (
        <NumberTextField
          numberValue={singleValue}
          onNumberChange={handleSingleValueChange}
          selectOnFocus
          sx={{ flexGrow: 1 }}
        />
      )}
      {valueControlType === "double" && (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <NumberTextField
            numberValue={fromValue}
            onNumberChange={handleFirstValueChange}
            selectOnFocus
            sx={{ flexGrow: 1 }}
          />
          <NumberTextField
            numberValue={toValue}
            onNumberChange={handleSecondValueChange}
            selectOnFocus
            sx={{ flexGrow: 1 }}
          />
        </Stack>
      )}
    </>
  );
};

export default ObjectNumberFilterEditorContent;
